import React, { useCallback, useEffect, useState } from "react";
import { getResearchPulseDigest } from "../../../Services/adminResearchPulse";
import { Card, CardContent, CardHeader, CardTitle } from "../../../ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../ui/select";
import { Skeleton } from "../../../ui/skeleton";
import ClusterTable from "./ClusterTable";
import {
  WINDOW_OPTIONS,
  errorMessage,
  formatCount,
  formatDateTime,
  sinceForDays,
} from "./researchPulseFormat";

const SECTIONS = [
  {
    key: "new_clusters",
    title: "New needs",
    empty: "No new clusters in this window.",
  },
  {
    key: "rising_clusters",
    title: "Rising needs",
    empty: "Nothing is rising.",
  },
  {
    key: "retrieval_bugs",
    title: "Retrieval bugs — route to product / QA",
    empty: "No retrieval bugs.",
    description:
      "A suitable Dialogue exists but wasn't found. These are not research gaps.",
  },
  {
    key: "top_candidates",
    title: "Top research candidates",
    empty: "No candidates.",
  },
];

/**
 * The four digest lists for a chosen window (the same digest the weekly email sends).
 * `reloadKey` refetches from the page's Refresh button without resetting the window.
 */
const DigestPanel = ({ onOpen, reloadKey }) => {
  const [windowDays, setWindowDays] = useState("7");
  const [digest, setDigest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const since = sinceForDays(windowDays);
      setDigest(await getResearchPulseDigest(since ? { since } : {}));
    } catch (err) {
      setError(errorMessage(err, "Failed to load the digest"));
      setDigest(null);
    } finally {
      setLoading(false);
    }
    // reloadKey forces a refetch from the page's Refresh button.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [windowDays, reloadKey]);

  useEffect(() => {
    load();
  }, [load]);

  const totals = digest?.totals || {};

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <p className="text-sm text-muted-foreground">
          {digest
            ? `Since ${formatDateTime(digest.since)} · ${formatCount(
                totals.clusters_seen
              )} clusters seen · ${formatCount(totals.signals)} signals`
            : "The weekly digest Science & Research receives by email."}
        </p>
        <Select value={windowDays} onValueChange={setWindowDays}>
          <SelectTrigger className="w-full sm:w-[180px]" aria-label="Digest window">
            <SelectValue placeholder="Window" />
          </SelectTrigger>
          <SelectContent>
            {WINDOW_OPTIONS.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {error && (
        <div className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive">
          {error}
        </div>
      )}

      {loading ? (
        <div className="space-y-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-24 w-full" />
          ))}
        </div>
      ) : (
        digest &&
        SECTIONS.map((section) => {
          const rows = digest[section.key] || [];
          return (
            <Card key={section.key}>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">
                  {section.title} ({rows.length})
                </CardTitle>
                {section.description && (
                  <p className="text-xs text-muted-foreground">{section.description}</p>
                )}
              </CardHeader>
              <CardContent>
                {rows.length === 0 ? (
                  <p className="text-sm text-muted-foreground">{section.empty}</p>
                ) : (
                  <ClusterTable rows={rows} onOpen={onOpen} />
                )}
              </CardContent>
            </Card>
          );
        })
      )}
    </div>
  );
};

export default DigestPanel;
